import { useObject } from 'data/hooks/objects'
import { useProcessFilter } from 'features/records/components/RecordFilters'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    RelatedRecordWidgetField,
    RelatedRecordWidgetFieldItem,
    RelatedRecordWidgetFieldRef,
    RelatedRecordWidgetType,
} from 'features/views/LayoutEditor/widgets/RelatedRecordWidget/types'
import { makeAllFields } from 'features/views/ListView/utils'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

export function useRelatedRecordWidgetTarget(widget: RelatedRecordWidgetType) {
    const { record } = useRecordManagerContext()
    const { fields, isEditing, object: localObject } = useLayoutEditorContext()

    const { targetField, fields: widgetFields = [], showAllFields } = widget.attrs

    const targetFieldType = targetField?.type ?? 'field'
    const isFieldRef = targetFieldType === 'field'

    const targetFieldDto = isFieldRef
        ? fields?.find(
              (f) => f.api_name === (targetField as RelatedRecordWidgetFieldRef)?.fieldApiName
          )
        : undefined
    const targetObjectSid = isFieldRef ? targetFieldDto?.link_target_object_id : localObject?._sid
    const targetFieldApiName = isFieldRef
        ? (targetField as RelatedRecordWidgetFieldRef)?.fieldApiName
        : '_sid'

    const { object: targetObject } = useObject(targetObjectSid)
    const targetObjectFields = makeAllFields(targetObject)

    let displayedFields: RelatedRecordWidgetFieldItem[]
    if (showAllFields) {
        const widgetFieldsByApiName = widgetFields.reduce((acc, f) => {
            return acc.set(f.apiName, f)
        }, new Map<string, RelatedRecordWidgetField>())

        displayedFields = targetObjectFields.reduce((acc, f) => {
            const widgetField = widgetFieldsByApiName.get(f.api_name)
            acc.push({
                ...widgetField,
                sid: f._sid,
                apiName: f.api_name,
                conditions: widgetField?.conditions ?? [],
                field: f,
            })

            return acc
        }, [] as RelatedRecordWidgetFieldItem[])
    } else {
        const targetFieldsByApiName = targetObjectFields.reduce((acc, f) => {
            return acc.set(f.api_name, f)
        }, new Map<string, FieldDto>())
        displayedFields = widgetFields.reduce((acc, f) => {
            const field = targetFieldsByApiName.get(f.apiName)
            if (field) {
                acc.push({
                    ...f,
                    field,
                })
            }

            return acc
        }, [] as RelatedRecordWidgetFieldItem[])
    }

    const processFilter = useProcessFilter()
    let visibleFields: RelatedRecordWidgetFieldItem[] = displayedFields
    if (!isEditing) {
        // If not editing, filter fields based on conditions.
        visibleFields = visibleFields.filter((f) => {
            if (!record && f.conditions.length) return false
            if (!record && !f.conditions.length) return true

            const filteredData = processFilter([record!], f.conditions)

            return filteredData.length > 0
        })
    }

    return useDeepEqualsMemoValue({
        displayedFields,
        visibleFields,
        targetObject,
        targetObjectFields,
        targetFieldApiName,
    })
}
